import React, { useState, useEffect } from 'react';
import { api, formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area
} from 'recharts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  TrendingUp,
  Package,
  Users,
  AlertTriangle,
  Wrench,
  Shield,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  DollarSign,
  Bell,
  Wallet,
  Activity,
  ShoppingBag,
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink
} from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { useNavigate } from 'react-router-dom';

function StatCard({ title, value, icon: Icon, trend, trendUp, color = 'primary', subValue }) {
  const colorStyles = {
    primary: 'bg-primary/5 text-primary border-primary/20',
    accent: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    destructive: 'bg-rose-50 text-rose-600 border-rose-200',
    warning: 'bg-amber-50 text-amber-600 border-amber-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    violet: 'bg-violet-50 text-violet-600 border-violet-200',
  };

  return (
    <Card className={`stat-card relative overflow-hidden transition-all duration-300 hover:shadow-lg border-l-4 ${colorStyles[color].replace('bg-', 'border-l-')}`}>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <div className="flex items-baseline gap-2">
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight font-mono">
                {value}
              </h3>
            </div>
            {subValue && (
              <p className="text-xs text-muted-foreground">{subValue}</p>
            )}
            {trend && (
              <div className={`flex items-center gap-1 text-xs font-medium ${trendUp ? 'text-emerald-600' : 'text-rose-600'}`}>
                {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                <span>{trend}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-2xl ${colorStyles[color]} shadow-sm`}>
            <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ title, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-4 mt-8 first:mt-0">
      <div className="p-1.5 bg-primary/10 rounded-lg">
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [salesChart, setSalesChart] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [expiringWarranties, setExpiringWarranties] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentStore, user, loading: storeLoading } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!storeLoading && !currentStore && (user?.role === 'super_admin' || user?.role === 'admin')) {
      navigate('/admin');
      return;
    }

    if (!storeLoading) {
      loadDashboardData();
    }
  }, [currentStore, storeLoading, navigate, user]);

  const loadDashboardData = async () => {
    try {
      const storeParam = currentStore?.id ? `store_id=${currentStore.id}` : '';
      const separator = storeParam ? '&' : '';

      const [statsRes, chartRes, topRes, lowStockRes, expiringRes] = await Promise.all([
        api.get(`/dashboard/stats?${storeParam}`),
        api.get(`/dashboard/sales-chart?days=7${separator}${storeParam}`),
        api.get(`/dashboard/top-products?limit=5${separator}${storeParam}`),
        api.get(`/products/low-stock?${storeParam}`),
        api.get(`/warranties/expiring?days=30${separator}${storeParam}`)
      ]);
      setStats(statsRes.data);
      setSalesChart(chartRes.data);
      setTopProducts(topRes.data);
      setLowStockProducts(lowStockRes.data);
      setExpiringWarranties(expiringRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-slate-500 animate-pulse">Po ngarkohet paneli...</p>
        </div>
      </div>
    );
  }

  const isTechStore = currentStore?.type !== 'boutique'; // Default to tech/general if not boutique
  const isAdmin = user?.role !== 'seller';

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in pb-10" data-testid="dashboard">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Paneli i Kontrollit
          </h1>
          <p className="text-slate-500 flex items-center gap-2 mt-1">
            <Calendar className="w-4 h-4" />
            {new Date().toLocaleDateString('sq-AL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Quick Actions (Future) */}
        {/* <div className="flex gap-2">
           <Button>Shitje e Re</Button>
        </div> */}
      </div>

      {/* Notifications Banner */}
      {(lowStockProducts.length > 0 || expiringWarranties.length > 0) && (
        <div className="grid gap-4 md:grid-cols-2">
          {lowStockProducts.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-4 shadow-sm">
              <div className="p-2 bg-amber-100 rounded-lg shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-amber-900">Kujdes: Stok i Ulët!</h4>
                <p className="text-sm text-amber-800 mt-1">
                  {lowStockProducts.length} produkte janë nën nivelin minimal.
                </p>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="link" className="p-0 h-auto text-amber-900 font-semibold underline mt-2 text-xs">
                      Shiko Listën e Plotë
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Produkte me Stok të Ulët</DialogTitle>
                    </DialogHeader>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Produkti</TableHead>
                            <TableHead>Stoku Aktual</TableHead>
                            <TableHead>Minimumi</TableHead>
                            <TableHead className="text-right">Veprime</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {lowStockProducts.map((product) => (
                            <TableRow
                              key={product.id}
                              className="cursor-pointer hover:bg-slate-50"
                              onClick={() => navigate(`/magazina?product_id=${product.id}&action=edit`)}
                            >
                              <TableCell className="font-medium">{product.name}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                                  {product.stock}
                                </Badge>
                              </TableCell>
                              <TableCell>{product.min_stock}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-amber-700 hover:text-amber-900 hover:bg-amber-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/magazina?product_id=${product.id}&action=edit`);
                                  }}
                                >
                                  Edito
                                  <ExternalLink className="w-3 h-3 ml-2" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          )}

          {expiringWarranties.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-4 shadow-sm">
              <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-900">Skadimi i Garancive</h4>
                <p className="text-sm text-blue-800 mt-1">
                  {expiringWarranties.length} garanci skadojnë së shpejti.
                  <span className="block mt-1 text-xs opacity-80">
                    Kontrolloni listën e garancive për njoftime.
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 1. FINANCIAL OVERVIEW */}
      <div>
        <SectionTitle title="Përmbledhje Financiare" icon={Wallet} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <StatCard
            title="Xhiro Ditore"
            value={formatCurrency(stats?.today_revenue || 0)}
            icon={TrendingUp}
            trend={`${stats?.today_sales_count || 0} shitje sot`}
            trendUp={true}
            color="primary"
          />
          {isAdmin && (
            <>
              <StatCard
                title="Fitimi Neto (Sot)"
                value={formatCurrency(stats?.today_profit || 0)}
                icon={DollarSign}
                color="accent"
                trend={stats?.today_revenue > 0 ? `${((stats?.today_profit / stats?.today_revenue) * 100).toFixed(1)}% marzh` : null}
                trendUp={stats?.today_profit > 0}
              />
              <StatCard
                title="Xhiro Mujore"
                value={formatCurrency(stats?.month_revenue || 0)}
                icon={CreditCard}
                color="violet"
                subValue="Të ardhurat për këtë muaj"
              />
              <StatCard
                title="Fitimi Neto (Muaj)"
                value={formatCurrency(stats?.month_profit || 0)}
                icon={DollarSign}
                color="accent"
                trend={stats?.month_revenue > 0 ? `${((stats?.month_profit / stats?.month_revenue) * 100).toFixed(1)}% marzh` : null}
                trendUp={stats?.month_profit > 0}
              />
              <StatCard
                title="Blerjet (Shpenzimet)"
                value={formatCurrency(stats?.month_purchases || 0)}
                icon={ShoppingBag}
                color="warning"
                subValue="Stoqe të reja këtë muaj"
              />
            </>
          )}
        </div>
      </div>

      {/* 2. OPERATIONAL METRICS */}
      <div>
        <SectionTitle title="Operacionet & Aktiviteti" icon={Activity} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <StatCard
            title="Inventari Total"
            value={stats?.total_products || 0}
            icon={Package}
            color="blue"
            trend={stats?.low_stock_products > 0 ? `${stats?.low_stock_products} stok i ulët` : 'Stoku OK'}
            trendUp={stats?.low_stock_products === 0}
          />

          {isAdmin && (
            <StatCard
              title="Borxhi i Klientëve"
              value={formatCurrency(stats?.customers_debt || 0)}
              icon={Users}
              color={stats?.customers_debt > 0 ? 'destructive' : 'accent'}
              subValue="Total i paarkëtuar"
            />
          )}

          {isTechStore && isAdmin && (
            <StatCard
              title="Servise Aktive"
              value={stats?.active_services || 0}
              icon={Wrench}
              color="primary"
              trend="Në proces"
              trendUp={true}
            />
          )}

          {isTechStore && isAdmin ? (
            <StatCard
              title="Garanci Aktive"
              value={stats?.active_warranties || 0}
              icon={Shield}
              color="accent"
              subValue="Garanci produktesh & servisi"
            />
          ) : (
            // For Boutique or non-tech stores, show something else or empty
            <StatCard
              title="Furnitorët"
              value={stats?.total_suppliers || 0}
              icon={Users}
              color="violet"
              subValue={`Borxhi: ${formatCurrency(stats?.suppliers_debt || 0)}`}
            />
          )}
        </div>
      </div>

      {/* 3. ANALYTICS CHARTS (Admin Only) */}
      {isAdmin && (
        <div>
          <SectionTitle title="Analiza e Performancës" icon={TrendingUp} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sales Chart - Takes up 2/3 */}
            <Card className="lg:col-span-2 shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle>Shitjet Ditore (7 Ditët e Fundit)</CardTitle>
                <CardDescription>Ecuria e të ardhurave për javën e fundit</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={salesChart}>
                      <defs>
                        <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#E52B50" stopOpacity={0.1} />
                          <stop offset="95%" stopColor="#E52B50" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis
                        dataKey="date"
                        stroke="#94a3b8"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        dy={10}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return date.toLocaleDateString('sq-AL', { weekday: 'short' });
                        }}
                      />
                      <YAxis
                        stroke="#94a3b8"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        dx={-10}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip
                        cursor={{ stroke: '#E52B50', strokeWidth: 1, strokeDasharray: '4 4' }}
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white border border-slate-100 p-3 rounded-lg shadow-xl ring-1 ring-slate-100">
                                <p className="text-xs text-slate-500 mb-1">{formatDate(label)}</p>
                                <p className="text-lg font-bold text-primary">
                                  {formatCurrency(payload[0].value)}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="total"
                        stroke="#E52B50"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorSales)"
                        activeDot={{ r: 6, strokeWidth: 0, fill: '#E52B50' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Top Products - Takes up 1/3 */}
            <Card className="shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle>Top Produktet</CardTitle>
                <CardDescription>Sipas të ardhurave</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topProducts} layout="vertical" barSize={20}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                      <XAxis type="number" hide />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={100}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + '...' : value}
                      />
                      <Tooltip
                        cursor={{ fill: '#f8fafc' }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white border border-slate-100 p-2 rounded-lg shadow-xl text-xs">
                                <p className="font-medium">{payload[0].payload.name}</p>
                                <p className="text-primary font-bold">{formatCurrency(payload[0].value)}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar
                        dataKey="revenue"
                        fill="#E52B50"
                        radius={[0, 4, 4, 0]}
                        background={{ fill: '#f1f5f9', radius: [0, 4, 4, 0] }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
